// Layer 1 (Data Schema, seed): the shared six-issue spine that Paul's two
// Module 3 mocks (driver-tree and role-dashboards) both project — one plant
// state, five role-specific readings. Stream A2 (`landings`) defines this
// copy from `module03_role_dashboards_3.html`; stream A1's driver-tree tree
// defines its own from the driver-tree mock. The orchestrator reconciles the
// two at merge (glass-poc-run/build-plan.md § Read in order, item 7).
//
// Facts are Paul's, copied verbatim (ASCII normalised: `um` -> `µm`,
// `C` -> `°C`, `-` -> `−` where a minus) per glass-poc-run/map-role-dashboards.md
// §3. The seed rulings from `decisions.md` § seed disagreements are applied
// here, not Paul's typed values:
//   - BM01 MTTR is 2.4 days, not the role-dashboards page's 4.1h
//   - CuSO4 contribution to the Zn recovery gap is 0.8 ppt
//   - issue #2's path uses the role-dashboards spelling (one tree, one node set)
// See discrepancies.md for the register rows.

import { FAILURE_MODES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { bm01BearingStopComparison } from '@/versions/v1/modules/downtime/ValueLost.transform';
import type { DriverIssueImpactType } from '@/versions/v1/schema/driver-tree';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';
import { REAGENT_UNIT_COSTS } from '@/versions/v1/modules/cost/seed';

const BM01_FMEA = FAILURE_MODES.find((row) => row.id === 'fm-trunnion')!;
const BM01_STOP = bm01BearingStopComparison();
const amount = (value: number) => `${CURRENCY_GLYPH}${value.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** The proposed SMBS dose increase behind issue 6 (Pb in Zn con): from the
 *  documented Pb circuit + Zn cleaner 1 dose (`REAGENT_UNIT_COSTS` smbs,
 *  475 g/t) to Paul's 530 g/t. The one field the Superintendent's approval
 *  gate (the dose is the Superintendent's gate) and the Senior metallurgist's
 *  issue row both read. `rd-reag-smbs` (195 g/t vs 200 design) is the Pb
 *  conditioner and rougher dosing point, a different addition point
 *  (discrepancies row 317). */
const SMBS_COST_ROW = REAGENT_UNIT_COSTS.find((row) => row.id === 'smbs')!;
export const SMBS_DOSE_PROPOSAL = {
  fromGpt: SMBS_COST_ROW.doseGramsPerTonne,
  toGpt: 530, // Paul's role-dashboards Senior met text (`475 to 530 g/t`)
} as const;
/** The increase as a whole percent (530 ÷ 475 − 1 = 11.6 → 12). */
export const SMBS_DOSE_PCT = Math.round((SMBS_DOSE_PROPOSAL.toGpt / SMBS_DOSE_PROPOSAL.fromGpt - 1) * 100);

export type IssueTier = 'T1' | 'T2' | 'T3' | 'Watch';
/** One vocabulary with the driver tree (D20): the union lives in its schema. */
export type IssueImpactType = DriverIssueImpactType;

export type IssueSpineRow = {
  rank: number;
  id: string;
  title: string;
  area: string;
  tier: IssueTier;
  impType: IssueImpactType;
  driverTreePath: string;
  /** Per-role differentiating text — reproduced verbatim from map-role-dashboards.md §3. */
  text: {
    cmet: string;
    smet: string;
    supt: string;
    maint: string;
    mgr: string;
  };
};

export const ISSUE_SPINE: readonly IssueSpineRow[] = [
  {
    rank: 1,
    id: 'iss-bm01-bearing',
    title: 'BM01 ball mill bearing — temperature trending',
    area: 'Grinding',
    tier: 'T1',
    impType: 'Risk',
    driverTreePath: 'EBITDA ▸ Mill availability ▸ BM01 health ▸ TE-622-625 bearing temp',
    text: {
      cmet:
        'BM01 bearing temperature Tier 1 active. TE-622-625 at {{BEARING_CURRENT}}°C vs {{BEARING_WARNING}}°C early warning / {{BEARING_LIMIT}}°C limit · {{BEARING_TREND}}. Oil pressure 285 kPa confirmed. Vibration check due 14:00. Oil sample to lab — Fe result 16:00. If Fe above 80 ppm: escalate to Tier 2.',
      smet:
        `Leading: BM-001 bearing wear (RPN ${BM01_FMEA.rpn}, 62% confidence). Competing: BM-009 lube oil viscosity 28%, BM-005 liner imbalance 10%. Discriminating test: oil Fe result at 16:00. Controlled inspection ${amount(BM01_STOP.plannedAmount)} (${BM01_STOP.plannedHours} h) vs avoided failure ${amount(BM01_STOP.avoidedFailureAmount)} (${BM01_STOP.avoidedFailureHours} h).`,
      supt:
        `Risk: ${BM01_STOP.avoidedFailureHours} h MTTR values an avoided failure at ${amount(BM01_STOP.avoidedFailureAmount)}. Confirm B. Fletcher allocated for 14:00 vibration check. Oil Fe result at 16:00 is the go/no-go on the controlled inspection window.`,
      maint:
        'PM-001 vibration check BM01 DE and NDE — due today, assigned B. Fletcher. Pre-stage SKF 23148 bearing kit before 16:00. If vibration above 20% of {{BASELINE_DAY}} baseline (3.6 mm/s): raise CMMS BM01-BRNG-001.',
      mgr:
        `Controlled inspection ${amount(BM01_STOP.plannedAmount)} (${BM01_STOP.plannedHours} h) vs avoided failure ${amount(BM01_STOP.avoidedFailureAmount)} (${BM01_STOP.avoidedFailureHours} h). BM01 is single-point failure for whole plant — no backup. Decision: confirm controlled inspection in this week’s window by EOD.`,
    },
  },
  {
    rank: 2,
    id: 'iss-p80-excursion',
    title: 'P80 excursion — ore hardness change',
    area: 'Grinding',
    tier: 'T2',
    impType: 'Active loss',
    driverTreePath: 'EBITDA ▸ Throughput and Zn rec ▸ Cyclone P80 ▸ Mill feed rate',
    text: {
      cmet:
        'Feed {{FEED}} stph vs {{FEED_DESIGN}} stph design since 07:40. PSA-001 P80 at {{P80}} µm vs {{P80_DESIGN}} µm design. Three readings stable. Log every 30 min.',
      smet:
        'Ore hardness at 78% confidence. The feed below design cuts throughput; coarse P80 also contributes to the Zn recovery shortfall. Monitor 4h before returning feed to its {{FEED_DESIGN}} stph design.',
      supt: 'If P80 not recovered by 13:00, contact mine planning for softer blend from {{BLEND_DAY}}.',
      maint: 'Monitor cyclone unit 4 spigot — at 11% wear (15% is replacement threshold).',
      mgr:
        '{{FEED}} stph vs {{FEED_DESIGN}} stph design is {{FEED_BELOW_PCT}}% below design. Mine-to-mill coordination is the highest-leverage action.',
    },
  },
  {
    rank: 3,
    id: 'iss-zn-recovery',
    title: 'Zn recovery below LSL',
    area: 'Zn Flotation',
    tier: 'T3',
    impType: 'Active loss',
    driverTreePath: 'EBITDA ▸ Zn NSR ▸ Zn recovery ▸ pH and CuSO4 and P80',
    text: {
      cmet:
        'Three concurrent factors: pH {{PH}} vs 11.0; CuSO4 {{CUSO4_BATCH}} g/L vs {{CUSO4_BATCH_DESIGN}}; P80 {{P80}} µm vs {{P80_DESIGN}} µm design. DO NOT adjust reagents without Met approval.',
      smet:
        'Attribution: pH, CuSO4 dose and P80 acting simultaneously — sequence CuSO4 batch first (~10:00), then pH recovery (2−4h lag), then P80 decision at 14:00.',
      supt:
        'Okoye managing the Zn recovery shortfall. Cannot close until {{RECOVERY_DAY}} assays show Zn above design floor.',
      maint: 'Support roles only.',
      mgr:
        'Zn recovery gap. MTD impact computed at demo cursor. Full recovery not expected until {{RECOVERY_DAY}} Day shift.',
    },
  },
  {
    rank: 4,
    id: 'iss-cuso4-batch',
    title: 'CuSO4 batch strength 162 g/L — below 180 g/L minimum',
    area: 'Reagents',
    tier: 'T1',
    impType: 'Active loss',
    driverTreePath: 'EBITDA ▸ Zn NSR ▸ Zn recovery ▸ CuSO4 activation',
    text: {
      cmet:
        'Reject current batch ({{CUSO4_BATCH}} g/L). New batch: 190 kg CuSO4 in 1000 L water, mix 30 min. Titrated at 09:45, target 185−200 g/L. FIT-CuSO4 dose should return to {{CUSO4_DOSE}} g/t within 5 min.',
      smet:
        'CuSO4 batch strength at 78%. 12% under the 185 g/L target. 1−2h before Zn recovery responds. Competing: pH suppressing activation at 14%.',
      supt: 'Approximately 0.8 pt of the Zn recovery gap. Control Met (Okoye) owns this action.',
      maint: 'Pump seal and strainer at the switch — CMMS CuSO4-PUMP-001.',
      mgr:
        `6 batch failures YTD at approximately ${CURRENCY_GLYPH}5,000 impact each = ${CURRENCY_GLYPH}30,000 YTD. Capital fix: CuSO4 pre-conditioning vessel (IMP-008, ${CURRENCY_GLYPH}95K capex, ${CURRENCY_GLYPH}680K NPV).`,
    },
  },
  {
    rank: 5,
    id: 'iss-lime-silo',
    title: 'Lime silo LT-500 at 26% — below 30% LSL',
    area: 'Reagents',
    tier: 'T2',
    impType: 'Risk',
    driverTreePath: 'EBITDA ▸ Zn NSR ▸ Zn recovery ▸ pH control ▸ LT-500',
    text: {
      cmet:
        'LT-500 at 26% vs 30% threshold. Delivery confirmed 21:00. Manual lime addition (bagged lime in shed, ~2h supply). Manual rates: Pb 4.2 kg/t, Zn 2.8 kg/t.',
      smet:
        'Lime silo depletion at 85% confidence. Lime reagent procedure applies. Recovery will lag 2−4h behind pH restoration.',
      supt:
        'Call supplier before 17:00. Night shift supervisor (M. Navarro) must be briefed at handover.',
      maint: 'Access route by 18:00. P100 respirators required. Dust suppression.',
      mgr:
        'Recurring lime depletion. Lime slaker upgrade IMP-003 business case reduces manual lime handling.',
    },
  },
  {
    rank: 6,
    id: 'iss-pb-in-zn-con',
    title: 'Pb in Zn con {{PB_IN_ZN}}% — trending toward 3% smelter penalty',
    area: 'Zn Flotation',
    tier: 'Watch',
    impType: 'Accruing penalty',
    driverTreePath: 'EBITDA ▸ Zn NSR deductions ▸ Pb penalty threshold ▸ ZnSO4/SMBS ratio',
    text: {
      cmet:
        'SA03 Pb in Zn con at {{PB_IN_ZN}}%. Trending up over 7 days. SMBS dose ratio declined 0.86 to 0.82.',
      smet:
        // polish.P27 comp adjudication 10: the headroom is derived at render
        // (smelter limit − Pb in Zn con, 3.0 − 2.6 = 0.4 pt), never typed; the
        // dose reads SMBS_DOSE_PROPOSAL (P27 r1).
        'Pb deportment at 72%. Propose SMBS dose increase {{SMBS_DOSE_PCT}}% ({{SMBS_DOSE_FROM}} to {{SMBS_DOSE_TO}} g/t) for the Superintendent\'s approval. {{PB_IN_ZN_HEADROOM}} pt headroom.',
      supt: `Monitor only — five more urgent issues above it. ${CURRENCY_GLYPH}600/day exposure — correctly ranked sixth.`,
      maint: 'No maintenance action required. Reagent selectivity issue.',
      mgr: 'If Pb in Zn con reaches 2.7% by end of week, recommend escalating to Tier 1 with Met.',
    },
  },
];

/** TARP tier -> the existing tone family (rule 5: tokens only, no new hex
 * family for Paul's tier chip colours). `Watch` reads as informational, not
 * a fourth alarm colour. */
export function tarpTierTone(tier: IssueTier): 'bad' | 'warn' | 'info' {
  if (tier === 'T1' || tier === 'T2') return 'bad';
  if (tier === 'T3') return 'warn';
  return 'info';
}

/** Issue-card accent — computed from rank/area, matching Paul's card
 * left-border groupings (rows 1-3 red, 4-5 amber, 6 blue) without a new hex
 * family: reds are `bad`, ambers `warn`, the watch-only row `info`. */
export function issueSpineTone(row: IssueSpineRow): 'bad' | 'warn' | 'info' {
  if (row.impType === 'Accruing penalty') return 'info';
  return row.area === 'Reagents' ? 'warn' : 'bad';
}
